import { z } from 'zod';
import type { Answer, ContactInput } from './answer.js';
import type { InternalBrief, InternalBriefOverseas } from './brief.js';
import { Lang, Track } from './common.js';
import type { Questionnaire } from './questionnaire.js';
import {
  SessionMode,
  type FollowupRecord,
  type FollowupView,
  type MemberCheckLine,
  type SessionStatus,
} from './session.js';

/* ------------------------------------------------------------------ *
 * Client routes: scoped by the session token in the URL, no login.
 * ------------------------------------------------------------------ */

export interface ClientConfig {
  /** Where the model runs; the consent text and privacy chip change with it. */
  llmMode: 'cloud' | 'local';
  /** Decision 1 in section 13: hold the client brief until a consultant releases it. */
  reviewGate: boolean;
  consentVersion: string;
  privacyEmail: string;
  bookingUrl: string | null;
  /** Months until the answers are anonymised (shown on the consent sheet). */
  retentionMonths: number;
  /** Where Atlas keeps the data (ATLAS_REGION), named on the cross-border consent page. */
  atlasRegion: 'hk' | 'sg';
}

/** GET /api/public-config (no login, no token): what /privacy fills the full notice in with. */
export type PublicConfig = Pick<ClientConfig, 'llmMode' | 'privacyEmail' | 'retentionMonths' | 'atlasRegion'>;

/** GET /api/auth/me: `user` is null when nobody is signed in, so the check itself never fails. */
export interface MeResponse {
  user: UserView | null;
}

/** GET /api/s/:token */
export interface ClientSessionResponse {
  session: {
    sessionCode: string;
    /** null until the client picks 企业 AI 转型 / 出海增长 on the welcome screen. */
    track: Track | null;
    lang: Lang;
    mode: SessionMode;
    status: SessionStatus;
    consent: { processing: boolean; crossBorder: boolean; contact: boolean; declined: boolean };
    currentQuestionId: string | null;
    hasContact: boolean;
    followups: FollowupView[];
    followupsReady: boolean;
    memberCheck: MemberCheckLine[] | null;
    /** Present once the client brief is visible to the client. */
    briefToken: string | null;
    consultantName: string | null;
    guidedByConsultant: boolean;
  };
  /**
   * The full bilingual version the session is pinned to, so the language toggle needs no round trip.
   * null while the track is not chosen; PATCH { track } pins it.
   */
  questionnaire: Questionnaire | null;
  answers: Record<string, Answer>;
  /** I4 as last saved, so the form can be edited on resume. Decrypted only for the token holder. */
  contact: ContactInput | null;
  config: ClientConfig;
}

export interface OkResponse {
  ok: true;
}

/**
 * Every error the API returns. `details` carries the failing paths of a validation error (for example
 * "modules.0.questions.3.options.2.label.en") so the dashboard can say which field is wrong.
 */
export interface ApiErrorBody {
  error: string;
  message?: string;
  details?: { path: string; message: string }[];
}

/**
 * 410 from GET /api/sessions/:id (and the other /api/sessions/:id routes) for a session whose data was
 * deleted at the client's request: only this tombstone is left. Anyone not assigned to it still gets 404.
 */
export interface SessionDeletedBody extends ApiErrorBody {
  error: 'deleted';
  sessionCode: string;
  deletedAt: string;
}

/**
 * POST /api/s/:token/send-brief and POST /api/sessions/:id/share-feishu. The request succeeds even when
 * nothing could be sent, so `sent` says what happened and `reason` why not.
 */
export interface DeliveryResponse {
  ok: true;
  sent: boolean;
  reason?: 'no_contact_consent' | 'no_email' | 'not_released' | 'not_configured' | 'failed';
}

/**
 * POST /api/sessions/:id/release: the brief is out either way. `emailed` says whether its link also went
 * to the client by email (only with the I5 contact consent), `emailReason` why not.
 */
export interface ReleaseResponse {
  ok: true;
  emailed: boolean;
  emailReason?: Exclude<DeliveryResponse['reason'], 'not_released'>;
  briefLink: string | null;
}

/** POST /api/s/:token/finish */
export interface FinishResponse {
  ok: true;
  status: SessionStatus;
}

/**
 * GET /api/b/:briefToken: the released client brief. While a regeneration of a released session runs, fails
 * or waits for review, the brief released before (404 not_ready only when nothing was ever released).
 */
export interface ClientBriefResponse {
  track: Track;
  lang: Lang;
  markdown: string;
  generatedAt: string;
  sessionCode: string;
  consultantName: string | null;
  recipient: string | null;
  privacyEmail: string;
  bookingUrl: string | null;
  sparse: boolean;
  llmMode: 'cloud' | 'local';
  /**
   * GET /api/b/:briefToken: the link can be emailed from this page (POST /api/b/:briefToken/send-brief),
   * because the client agreed to contact (I5) and left an email. The in-flow brief works it out itself.
   */
  canEmail?: boolean;
}

/** Server-sent events on GET /api/s/:token/events. */
export type SessionEvent =
  | { type: 'status'; status: SessionStatus }
  | { type: 'followups'; followups: FollowupView[] }
  | { type: 'progress'; phase: 'followups' | 'internal' | 'client'; progress: number }
  | { type: 'brief_delta'; text: string }
  | { type: 'brief_reset' }
  | { type: 'brief_done'; markdown: string; briefToken: string }
  | { type: 'awaiting_review' }
  | { type: 'error'; code: string; message: string };

/* ------------------------------------------------------------------ *
 * Consultant routes: JWT in an httpOnly cookie.
 * ------------------------------------------------------------------ */

export const LoginInput = z.object({ email: z.email(), password: z.string().min(8).max(200) }).strict();
export type LoginInput = z.infer<typeof LoginInput>;

export interface UserView {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'consultant';
  feishuOpenId?: string | null;
}

export const CreateUserInput = z
  .object({
    email: z.email(),
    name: z.string().min(1).max(60),
    password: z.string().min(10).max(200),
    role: z.enum(['admin', 'consultant']),
    feishuOpenId: z.string().max(80).optional(),
  })
  .strict();
export type CreateUserInput = z.infer<typeof CreateUserInput>;

export const CreateSessionInput = z
  .object({
    /** Omit (or null) to let the client choose on the welcome screen. */
    track: Track.nullable().optional(),
    lang: Lang.default('zh'),
    mode: SessionMode.default('self'),
    /** Consultant-only label ("the trading firm from the Shenzhen meetup"); never sent to the model. */
    label: z.string().max(120).optional(),
    /** Admins may assign to another consultant. */
    consultantId: z.string().max(40).optional(),
  })
  .strict();
export type CreateSessionInput = z.infer<typeof CreateSessionInput>;

export interface CreateSessionResponse {
  session: SessionListItem;
  link: string;
  qrDataUrl: string;
}

export interface SessionListItem {
  id: string;
  sessionCode: string;
  track: Track | null;
  label: string | null;
  lang: Lang;
  mode: SessionMode;
  status: SessionStatus;
  consultantId: string;
  consultantName: string | null;
  industry: string | null;
  completion: number;
  overallConfidence: 'high' | 'medium' | 'low' | null;
  fit: 'strong' | 'possible' | 'weak' | null;
  flagged: boolean;
  llmMode: 'cloud' | 'local';
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  /**
   * When the questionnaire link stops opening (the client then sees 410 expired): the same instant as
   * expiresAt, named for what it means. Always sent by the API; POST /api/sessions/:id/new-link renews it.
   */
  linkExpiresAt?: string;
}

/**
 * POST /api/sessions/:id/new-link (the assigned consultant or an admin): a fresh questionnaire link for the
 * same session, valid 30 days, so the client resumes their saved answers. The previous link stops working.
 * 410 deleted or anonymised once the session's data is gone.
 */
export interface NewLinkResponse {
  link: string;
  expiresAt: string;
}

export interface BriefRecordView {
  id: string;
  kind: 'internal' | 'client';
  /** The brief the client sees and the pipeline uses, or a consultant-requested run on another model. */
  role: 'primary' | 'second_opinion';
  track: Track;
  status: 'pending' | 'ready' | 'failed' | 'raw_fallback';
  lang: Lang;
  model: string;
  promptVersion: string;
  schemaVersion: number;
  json: InternalBrief | InternalBriefOverseas | null;
  /** Client brief markdown by language; internal briefs may carry an on-demand translation. */
  markdown: Partial<Record<Lang, string>>;
  translations: Partial<Record<Lang, InternalBrief | InternalBriefOverseas>>;
  rawText: string | null;
  issues: string[];
  tokensIn: number | null;
  tokensOut: number | null;
  latencyMs: number | null;
  releasedAt: string | null;
  edits: { at: string; by: string; path: string }[];
  createdAt: string;
}

/**
 * One consent screen's current decision. `verbal` and `takenBy` (the consultant's name) are set when a
 * consultant took it in guided mode; a later decision the client makes alone replaces the record without them.
 */
export interface ConsentStepView {
  acceptedAt?: string;
  declinedAt?: string;
  textVersion?: string;
  ip?: string;
  verbal?: boolean;
  takenBy?: string;
}

export interface SessionDetailResponse {
  session: SessionListItem & {
    consent: {
      processing: ConsentStepView | null;
      crossBorder: ConsentStepView | null;
      contact: { acceptedAt?: string; textVersion?: string } | null;
      /**
       * Derived from processing and crossBorder: set only while every decision on record was taken verbally
       * by a consultant (this one). Who took each decision is on the records themselves.
       */
      takenBy: string | null;
      verbal: boolean;
    };
    questionnaireVersion: number;
    memberCheck: MemberCheckLine[] | null;
    followups: FollowupRecord[];
    progress: { currentQuestionId: string | null; startedAt: string | null; completedAt: string | null; lastSeenAt: string | null };
    deletionRequested: boolean;
  };
  questionnaire: Questionnaire | null;
  answers: Record<string, Answer>;
  /** Decrypted for the assigned consultant and admins; every read is written to the audit log. */
  contact: ContactInput | null;
  briefs: {
    internal: BriefRecordView | null;
    client: BriefRecordView | null;
    /** Runs of the same stored input on other models, newest first; never shown to the client. */
    secondOpinions: BriefRecordView[];
  };
  /** The questionnaire link (30 days). */
  link: string | null;
  /**
   * The brief link (12 months) once a brief is released: what to send when the email did not go out. It
   * stays while a regeneration runs, fails or waits for review: the link shows the brief released before.
   */
  briefLink: string | null;
}

/**
 * Every model call is recorded with its exact (pseudonymised) input and raw output, so any session can be
 * re-run on another model and compared, and an eval run can replay real sessions.
 */
export interface LlmCallView {
  id: string;
  sessionId: string | null;
  briefId: string | null;
  jobType: 'followups' | 'brief_internal' | 'brief_client' | 'translate_internal' | 'repair' | 'eval';
  model: string;
  llmMode: 'cloud' | 'local';
  promptId: string;
  structured: 'format' | 'prompt' | 'none';
  /** The messages exactly as sent: contain answers, never contact details. */
  messages: { role: 'system' | 'user' | 'assistant'; content: string }[];
  rawOutput: string;
  ok: boolean;
  error: string | null;
  tokensIn: number | null;
  tokensOut: number | null;
  latencyMs: number;
  /**
   * A follow-up call sent before the contact was known (or its repair): its stored messages and output are
   * what was sent, less the name, company and contact details found once the client gave them.
   */
  scrubbedAfterSend: boolean;
  createdAt: string;
}

/** POST /api/sessions/:id/second-opinion: re-runs the stored input on another model. */
export const SecondOpinionInput = z
  .object({
    model: z.string().min(1).max(80),
    kinds: z.array(z.enum(['internal', 'client'])).min(1).max(2).default(['internal']),
  })
  .strict();
export type SecondOpinionInput = z.infer<typeof SecondOpinionInput>;

/** GET /api/config: what the dashboard needs to offer. */
export interface ConsultantConfigResponse {
  defaultModel: string;
  /** Models offered for a second opinion (LLM_COMPARE_MODELS). */
  compareModels: string[];
  /**
   * Where each of defaultModel and compareModels runs: 'cloud' for an Ollama cloud tag (`…:…cloud`,
   * `…-cloud`) or any model while OLLAMA_HOST is ollama.com. A session whose llmMode is 'local' is never
   * sent to a 'cloud' model: POST /second-opinion, /regenerate and /translate answer 422 model_not_allowed.
   */
  modelModes?: Record<string, 'cloud' | 'local'>;
  /**
   * 'server': GET /api/sessions/:id/export.pdf renders the PDF with Chromium. 'browser': this server has no
   * Chromium (the slim all-in-one image), so the dashboard opens the print page and the consultant's
   * browser saves the PDF.
   */
  pdfExport?: 'server' | 'browser';
  llmMode: 'cloud' | 'local';
  reviewGate: boolean;
  tracks: Track[];
}

export const PatchBriefInput = z
  .object({
    /** Internal brief: replace the JSON (validated against InternalBrief). */
    json: z.unknown().optional(),
    /** Client brief: replace the markdown for a language. */
    markdown: z.object({ lang: Lang, text: z.string().min(1).max(20000) }).optional(),
  })
  .strict()
  .refine((v) => v.json !== undefined || v.markdown !== undefined, 'json or markdown required');
export type PatchBriefInput = z.infer<typeof PatchBriefInput>;

export const TranslateInput = z.object({ lang: Lang }).strict();

export interface QuestionnaireVersionItem {
  track: Track;
  version: number;
  status: 'draft' | 'published';
  publishedAt: string | null;
  updatedAt: string;
  questionCount: number;
}

export interface AnalyticsQuestionRow {
  questionId: string;
  module: string;
  shown: number;
  answered: number;
  skipped: number;
  unsure: number;
  /** Sessions whose last answered question was this one and that never finished. */
  dropOff: number;
  medianMs: number | null;
}

export interface AnalyticsResponse {
  track: Track;
  version: number;
  sessions: { total: number; consented: number; completed: number; declined: number; medianMinutes: number | null };
  modules: { module: string; medianMs: number | null }[];
  questions: AnalyticsQuestionRow[];
}

export interface DeletionRequestView {
  id: string;
  sessionId: string;
  sessionCode: string;
  requestedAt: string;
  processedAt: string | null;
  reason: string | null;
}

export const EvalScoreInput = z
  .object({
    sessionKey: z.string().max(80),
    /** Score a specific model's brief within the run (bake-offs run several models over the same set). */
    model: z.string().max(80).optional(),
    scores: z.object({
      realPain: z.number().int().min(1).max(5),
      ranking: z.number().int().min(1).max(5),
      firstStep: z.number().int().min(1).max(5),
      nothingInvented: z.number().int().min(1).max(5),
      readsNaturally: z.number().int().min(1).max(5),
      callQuestions: z.number().int().min(1).max(5),
    }),
    note: z.string().max(1000).optional(),
  })
  .strict();
export type EvalScoreInput = z.infer<typeof EvalScoreInput>;
